import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  FiHome,
  FiUser,
  FiCreditCard,
  FiPackage,
  FiShare2,
  FiGift,
  FiHelpCircle,
  FiClipboard,
  FiFileText,
  FiUsers,
  FiTrendingUp,
  FiMessageCircle,
  FiChevronsLeft,
  FiChevronsRight,
  FiShield,
  FiSettings,
} from "react-icons/fi";
import { useWebSocket } from "../context/WebSocketContext";

const navItems = [
  { label: "Dashboard", path: "/dashboard", icon: FiHome, scopes: ["normal", "admin"] },
  { label: "Profile", path: "/profile", icon: FiUser, scopes: ["normal", "admin"] },
  { label: "Bank Details", path: "/bank-details", icon: FiCreditCard, scopes: ["normal", "admin"] },
  { label: "Products", path: "/products", icon: FiPackage, scopes: ["normal", "admin"] },
  { label: "Network", path: "/network", icon: FiShare2, scopes: ["normal", "admin"] },
  { label: "Network Commission", path: "/network-commission", icon: FiTrendingUp, scopes: ["admin"] },
  { label: "Incentive", path: "/incentive", icon: FiGift, scopes: ["normal", "admin"] },
  { label: "Messages", path: "/messages", icon: FiMessageCircle, scopes: ["normal", "admin"] },
  { label: "FAQs", path: "/faqs", icon: FiHelpCircle, scopes: ["admin"] },
  { label: "Underwriting", path: "/underwriting", icon: FiClipboard, scopes: ["admin"] },
  { label: "Batch Payment History", path: "/batch-payment-history", icon: FiFileText, scopes: ["admin"] },
  {
    label: "Batch Enrollment History",
    path: "/batch-enrollment-amaphil-id-history",
    icon: FiFileText,
    scopes: ["admin"],
  },
  { label: "Batch Utilizations History", path: "/batch-utilizations-history", icon: FiFileText, scopes: ["admin"] },
  { label: "Batch Payout History", path: "/batch-payout-history", icon: FiFileText, scopes: ["admin"] },
  { label: "Members", path: "/members", icon: FiUsers, scopes: ["admin"] },
  { label: "Incentive (A)", path: "/incentive-a", icon: FiTrendingUp, scopes: ["admin"] },
  { label: "Contact Us Messages", path: "/contact-us-messages", icon: FiHelpCircle, scopes: ["admin"] },
  { label: "System Settings", path: "/system-settings", icon: FiSettings, scopes: ["system_admin"] },
  { label: "OAuth Users", path: "/system-oauth-users", icon: FiUsers, scopes: ["system_admin"] },
];

const Sidebar = ({ isOpen, isCollapsed, onClose, onToggleCollapse }) => {
  const scopeValue = localStorage.getItem("rmc2.scope") || "";
  const scopes = scopeValue
    .split(" ")
    .map((scope) => scope.trim())
    .filter(Boolean);
  const isAdmin = scopes.includes("admin");
  const isSystemAdmin = scopes.includes("system_admin");
  const visibleItems = navItems.filter((item) => {
    if (item.scopes.includes("system_admin")) {
      return isSystemAdmin;
    }
    return isAdmin || item.scopes.includes("normal");
  });
  const [isBatchOpen, setIsBatchOpen] = useState(false);
  const [isSystemLogsOpen, setIsSystemLogsOpen] = useState(false);
  const { unreadCount } = useWebSocket();

  const batchItems = [
    {
      label: "Batch Payment History",
      path: "/batch-payment-history",
      icon: FiFileText,
      scopes: ["admin"],
    },
    {
      label: "Batch Enrollment History",
      path: "/batch-enrollment-amaphil-id-history",
      icon: FiFileText,
      scopes: ["admin"],
    },
    {
      label: "Batch Utilizations History",
      path: "/batch-utilizations-history",
      icon: FiFileText,
      scopes: ["admin"],
    },
    {
      label: "Batch Payout History",
      path: "/batch-payout-history",
      icon: FiFileText,
      scopes: ["admin"],
    },
  ];
  const systemLogsItems = [
    {
      label: "Register Module Logs",
      path: "/system-logs/register",
      icon: FiShield,
      scopes: ["system_admin"],
    },
    {
      label: "Product Purchase Module Logs",
      path: "/system-logs/product-purchase",
      icon: FiShield,
      scopes: ["system_admin"],
    },
    {
      label: "Underwriting Module Logs",
      path: "/system-logs/underwriting",
      icon: FiShield,
      scopes: ["system_admin"],
    },
  ];

  return (
    <>
      <button
        type="button"
        aria-label="Close navigation menu"
        onClick={onClose}
        className={`fixed inset-0 z-30 bg-slate-950/50 transition lg:hidden ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        className={`fixed inset-y-0 left-0 z-40 bg-[var(--brand-orange)] text-white shadow-xl transition-all lg:static lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } ${isCollapsed ? "w-20" : "w-72"}`}
      >
        <div className={`pb-6 pt-8 ${isCollapsed ? "px-3" : "px-6"}`}>
          <p
            className={`text-sm uppercase tracking-[0.2em] text-white/70 ${
              isCollapsed ? "text-center text-[10px]" : ""
            }`}
          >
            Menu
          </p>
        </div>
        <nav className="flex flex-col gap-1 px-3 pb-8">
          {visibleItems
            .filter(
              (item) =>
                ![
                  "/batch-payment-history",
                  "/batch-enrollment-amaphil-id-history",
                  "/batch-utilizations-history",
                  "/batch-payout-history",
                ].includes(item.path)
            )
            .map((item) => {
            const Icon = item.icon;
            const isMessages = item.path === "/messages";
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-4 py-3 text-[15px] font-medium transition ${
                    isActive
                      ? "bg-[var(--brand-orange-dark)] text-white shadow-md"
                      : "text-white/90 hover:bg-white/10"
                  }`
                }
              >
                <Icon className="text-lg" />
                <span className={`${isCollapsed ? "hidden" : "inline"}`}>
                  {item.label}
                </span>
                {isMessages && unreadCount > 0 && (
                  <span
                    className={`ml-auto rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-[var(--brand-orange)] ${
                      isCollapsed ? "hidden" : "inline-flex"
                    }`}
                  >
                    {unreadCount}
                  </span>
                )}
              </NavLink>
            );
          })}
          {isAdmin && (
            <div>
              <button
                type="button"
                onClick={() => setIsBatchOpen((prev) => !prev)}
                className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-[15px] font-medium transition ${
                  isBatchOpen ? "bg-white/10 text-white" : "text-white/90 hover:bg-white/10"
                }`}
              >
                <FiFileText className="text-lg" />
                <span className={`${isCollapsed ? "hidden" : "inline"}`}>
                  Upload History Logs
                </span>
                <span className={`ml-auto ${isCollapsed ? "hidden" : "inline"}`}>
                  <FiChevronsRight
                    className={`transition-transform duration-300 ${
                      isBatchOpen ? "rotate-90" : "rotate-0"
                    }`}
                  />
                </span>
              </button>
              <div
                className={`overflow-hidden pl-10 transition-all duration-300 ${
                  isBatchOpen && !isCollapsed
                    ? "mt-1 max-h-96 translate-y-0 opacity-100"
                    : "max-h-0 -translate-y-1 opacity-0 pointer-events-none"
                }`}
              >
                <div className="space-y-1">
                  {batchItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                          isActive
                            ? "bg-[var(--brand-orange-dark)] text-white shadow-md"
                            : "text-white/90 hover:bg-white/10"
                        }`
                      }
                    >
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          )}
          {isSystemAdmin && (
            <div>
              <button
                type="button"
                onClick={() => setIsSystemLogsOpen((prev) => !prev)}
                className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-[15px] font-medium transition ${
                  isSystemLogsOpen ? "bg-white/10 text-white" : "text-white/90 hover:bg-white/10"
                }`}
              >
                <FiShield className="text-lg" />
                <span className={`${isCollapsed ? "hidden" : "inline"}`}>
                  System Logs Tracking
                </span>
                <span className={`ml-auto ${isCollapsed ? "hidden" : "inline"}`}>
                  <FiChevronsRight
                    className={`transition-transform duration-300 ${
                      isSystemLogsOpen ? "rotate-90" : "rotate-0"
                    }`}
                  />
                </span>
              </button>
              <div
                className={`overflow-hidden pl-10 transition-all duration-300 ${
                  isSystemLogsOpen && !isCollapsed
                    ? "mt-1 max-h-96 translate-y-0 opacity-100"
                    : "max-h-0 -translate-y-1 opacity-0 pointer-events-none"
                }`}
              >
                <div className="space-y-1">
                  {systemLogsItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                          isActive
                            ? "bg-[var(--brand-orange-dark)] text-white shadow-md"
                            : "text-white/90 hover:bg-white/10"
                        }`
                      }
                    >
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          )}
        </nav>
        <div className="mt-auto px-3 pb-6">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/15 px-4 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/25"
          >
            {isCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
            <span className={`${isCollapsed ? "hidden" : "inline"}`}>
              {isCollapsed ? "Expand" : "Collapse"}
            </span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
